import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType } from '@econtractor/types';
import type { CloudUploadScope } from 'api/apiCloudflare';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { FilesContainer } from 'modules/files';

interface VendorFormFilesProps {
    vendorId: number;
    files: LeadCloudFileType[];
    onFilesChange: (nextFiles: LeadCloudFileType[]) => Promise<boolean>;
    onEnsureEntity?: () => Promise<CloudUploadScope | false>;
}

const VendorFormFiles = ({ vendorId, files, onFilesChange, onEnsureEntity }: VendorFormFilesProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Vendors', 'FileNavigator', 'common']);
    const vendorIdRef = useRef(vendorId);
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');

    useEffect(() => {
        vendorIdRef.current = vendorId;
    }, [vendorId]);

    const handleEnsureEntity = async (): Promise<CloudUploadScope | false> => {
        if (!onEnsureEntity) return false;
        const scope = await onEnsureEntity();
        if (!scope) return false;
        const createdId = Number(scope.vendorId || 0);
        if (createdId > 0) vendorIdRef.current = createdId;
        return scope;
    };
 
    return (
        <FilesContainer
            files={files}
            scope={{ vendorId }}
            canEdit={canEdit}
            addButtonLabel={t('btnAddFiles')}
            uploadingLabel={t('common:buttons.uploading')}
            saveFirstMessage={t('messSaveVendorFirst')}
            errorMessage={t('common:messError')}
            uploadSuccessMessage={t('FileNavigator:messDownloadSuccess')}
            onFilesChange={onFilesChange}
            onEnsureEntity={vendorId <= 0 ? handleEnsureEntity : undefined}
        />
    );
};

export default VendorFormFiles;
